import React, { useEffect, useState } from 'react';
import { X, Zap, Square, AlertTriangle } from 'lucide-react';

// Hedge tamamlayıcı popup'ı — bir bacağı bitmiş pozisyonun karşı deltasını doldurur.
//
// Referans fiyat SABİT (karşı bacak zaten yapılmış), o yüzden bu tek taraflı bir icra
// problemi: "şu tavanın altında, sabırla, dilim dilim doldur". Motor kovalamaz — tavan
// aşılırsa bekler; amaç hızlı dolmak değil basis'i eksiye çevirmemek.
//
// EMİR GÖNDERİR. Ateşlemeden önce özet gösteriliyor; tavanlar backend'de (klip sayısı,
// notional, zaman aşımı) mantıktan bağımsız sert sınırlar olarak duruyor.

export interface HedgePrefill {
  groupNo: number;
  symbol: string;          // hedef borsadaki sembol
  base: string;            // çıplak coin adı (borsa değişince çeviri buradan yapılır)
  side: 'long' | 'short';  // TAMAMLANACAK bacağın yönü
  selfBotId?: string;      // AÇIK bacağın borsası — hedef listesinden çıkarılır
  quantity: number;
  refPrice: number;        // yapılmış bacağın ortalama fiyatı
  refLabel: string;        // "Extended · SHORT 75.000 @ 0.015154"
  botId: string;           // hedef borsa (bot)
}

interface Props {
  dark?: boolean;
  prefill: HedgePrefill;
  bots: { id: string; name: string }[];
  symbolFor: (botId: string, base: string) => string;
  sendWs: (m: any) => void;
  subscribe: (fn: (m: any) => void) => (() => void) | undefined;
  onClose: () => void;
}

// Komisyon tamponu: ham referans fiyatı tavan yapmak iyimser — iki bacağın taker
// komisyonu basis'i eksiye çevirebilir. Varsayılan ~6 bps, kullanıcı değiştirebilir.
const DEFAULT_OFFSET_BPS = 6;

export default function HedgeCompleteModal({ dark = true, prefill, bots, symbolFor, sendWs, subscribe, onClose }: Props) {
  const [botId, setBotId]   = useState(prefill.botId);
  const [qty, setQty]       = useState(String(prefill.quantity));
  const [offset, setOffset] = useState(String(DEFAULT_OFFSET_BPS));
  const [taker, setTaker]   = useState(true);
  const [delayS, setDelayS] = useState('3');   // klipler arası bekleme (sn)
  // Klip boyutu — boş bırakılırsa motor görünen derinliğe göre seçer.
  // Alan yokken "hepsini tek klipte al" denemiyordu.
  const [clipSz, setClipSz] = useState('');
  const [timeout_, setTimeout_] = useState('10');
  const [job, setJob]       = useState<any>(null);
  // Sembol BORSAYA bağlı. Kullanıcı borsayı değiştirince eski borsanın sembolünü
  // göndermek sessiz bir başarısızlıktı: hedef borsa pariteyi tanımıyor, boş defter
  // dönüyor, motor emir atmadan zaman aşımına kadar dönüyordu.
  const sym = botId === prefill.botId
    ? prefill.symbol
    : symbolFor(botId, prefill.base);
  const [msg, setMsg]       = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy]     = useState(false);

  const card  = dark ? 'bg-[#0a1628] border-[#164a9e]' : 'bg-white border-[#d4e0f0]';
  const sub   = dark ? 'bg-[#061530] border-[#0a2560]' : 'bg-[#eef3fa] border-[#d4e0f0]';
  const muted = dark ? 'text-blue-300/70' : 'text-slate-500';
  const inp   = dark
    ? 'bg-[#03112b] border-[#0a2560] text-blue-50'
    : 'bg-white border-[#d4e0f0] text-slate-800';

  // Tavan: long tamamlıyorsak referansın ALTINDA, short ise ÜSTÜNDE almalıyız.
  const bps   = Number(offset) || 0;
  const limit = prefill.side === 'long'
    ? prefill.refPrice * (1 - bps / 10000)
    : prefill.refPrice * (1 + bps / 10000);

  useEffect(() => {
    const unsub = subscribe((m: any) => {
      if (m.type === 'hedge_started') {
        setBusy(false);
        if (m.payload?.success) {
          setMsg({ ok: true, text: `İş başladı — dolum Grup ${prefill.groupNo}'e otomatik bağlanacak.` });
          poll();
        }
        else setMsg({ ok: false, text: m.payload?.error || 'başlatılamadı' });
      } else if (m.type === 'hedge_status') {
        // Yalnız sembol ile eşlemek yetmez: iki borsada aynı sembolde iş varsa
        // yanlış ilerleme gösterirdi.
        const mine = (m.payload?.jobs || []).find(
          (j: any) => j.symbol === sym && (!j.botId || j.botId === botId));
        setJob(mine || null);
      } else if (m.type === 'hedge_stopped') {
        setMsg({ ok: true, text: 'Durdurma gönderildi.' });
      }
    });
    return () => unsub?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe, sym, botId]);

  const poll = () => sendWs({ type: 'hedge_status', payload: { botId, symbol: sym } });
  useEffect(() => {
    poll();
    const id = setInterval(poll, 2000);   // canlı ilerleme
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId]);

  const fire = () => {
    if (!sym) { setMsg({ ok: false, text: `${prefill.base} bu borsada bulunamadı.` }); return; }
    const q = Number(qty);
    if (!q || q <= 0) { setMsg({ ok: false, text: 'Miktar pozitif olmalı.' }); return; }
    setBusy(true); setMsg(null);
    sendWs({ type: 'hedge_start', payload: {
      // groupNo ŞART: backend dolumu bu gruba bağlıyor. Eskiden gönderilmiyordu,
      // işlem gruba düşmüyor, elle bağlamak gerekiyordu.
      botId, symbol: sym, side: prefill.side, quantity: q, groupNo: prefill.groupNo,
      limitPrice: limit, takerAllowed: taker,
      clipDelayS: Number(delayS) || 1, timeoutMin: Number(timeout_) || 10,
      clipSize: Number(clipSz) || 0,
    } });
  };

  const running = job?.state === 'running';
  const pct = job ? Math.min(100, 100 * (job.filled || 0) / (job.target || 1)) : 0;
  const nf = (n: number, d = 6) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: d });

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className={`w-[520px] max-w-full rounded-xl border ${card} p-4 shadow-2xl`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3">
          <Zap size={15} className="text-amber-400" />
          <span className="font-semibold text-sm">Karşı bacağı tamamla — Grup {prefill.groupNo}</span>
          <button onClick={onClose} className={`ml-auto ${muted} hover:text-rose-400`}><X size={16} /></button>
        </div>

        <div className={`rounded-lg border ${sub} p-2.5 mb-3 text-[11px] space-y-1`}>
          <div className={muted}>Yapılmış bacak</div>
          <div className="font-mono">{prefill.refLabel}</div>
          <div className={`${muted} pt-1`}>Tamamlanacak</div>
          <div className="font-mono">
            <span className={prefill.side === 'long' ? 'text-emerald-400' : 'text-rose-400'}>
              {prefill.side.toUpperCase()}
            </span>{' '}
            {nf(Number(qty), 4)} {sym || '—'}
            <span className={muted}> · tavan </span>
            <span className="text-amber-400">{nf(limit)}</span>
            <span className={muted}> ({prefill.side === 'long' ? 'altından alınacak' : 'üstünden satılacak'})</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-[11px]">
          <label className="flex flex-col gap-1">
            <span className={muted}>Borsa</span>
            <select value={botId} onChange={e => setBotId(e.target.value)} disabled={running}
              className={`px-2 py-1.5 rounded-lg border ${inp}`}>
              <option value="">— borsa seç —</option>
              {bots.filter(b => b.id !== prefill.selfBotId)
                   .map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className={muted}>Miktar</span>
            <input value={qty} onChange={e => setQty(e.target.value)} disabled={running}
              className={`px-2 py-1.5 rounded-lg border font-mono ${inp}`} />
          </label>
          <label className="flex flex-col gap-1">
            <span className={muted} title="Referans fiyattan bu kadar içeride kal — komisyonu karşılar">
              Tavan ofseti (bps)
            </span>
            <input value={offset} onChange={e => setOffset(e.target.value)} disabled={running}
              className={`px-2 py-1.5 rounded-lg border font-mono ${inp}`} />
          </label>
          <label className="flex flex-col gap-1">
            <span className={muted} title="Her emirde gönderilecek miktar. Boş = görünen derinliğe göre otomatik.">
              Klip boyutu
              {!clipSz && <span className="opacity-60"> · oto</span>}
            </span>
            <input value={clipSz} onChange={e => setClipSz(e.target.value)} disabled={running}
              placeholder={`oto (${nf(Number(qty), 4)} = tek klip)`}
              className={`px-2 py-1.5 rounded-lg border font-mono ${inp}`} />
          </label>
          <label className="flex flex-col gap-1">
            <span className={muted} title="İki klip arasındaki bekleme. Zaman aşımından büyük olmamalı.">
              Klip arası (sn)
            </span>
            <input value={delayS} onChange={e => setDelayS(e.target.value)} disabled={running}
              className={`px-2 py-1.5 rounded-lg border font-mono ${inp}`} />
          </label>
          <label className="flex flex-col gap-1">
            <span className={muted}>Zaman aşımı (dk)</span>
            <input value={timeout_} onChange={e => setTimeout_(e.target.value)} disabled={running}
              className={`px-2 py-1.5 rounded-lg border font-mono ${inp}`} />
          </label>
          <label className="flex items-end gap-2 pb-1">
            <button onClick={() => setTaker(v => !v)} disabled={running}
              className={`px-2.5 py-1.5 rounded-lg border transition-colors ${taker
                ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-400'
                : `${sub} ${muted}`}`}>
              Taker izni {taker ? 'açık' : 'kapalı'}
            </button>
          </label>
        </div>

        {!sym && (
          <p className="mt-2 text-[11px] text-rose-400 flex items-start gap-1.5">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            {prefill.base} bu borsada listeli değil — başka borsa seç.
          </p>
        )}

        {Number(delayS) > Number(timeout_) * 60 && (
          <p className="mt-2 text-[11px] text-amber-400 flex items-start gap-1.5">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            Klip arası ({delayS} sn) zaman aşımından ({timeout_} dk) uzun — tek klip bile denenmeyebilir.
          </p>
        )}

        {!taker && (
          <p className="mt-2 text-[11px] text-amber-400 flex items-start gap-1.5">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            Taker kapalıyken motor emir göndermez — şimdilik yalnız taker modu destekleniyor.
          </p>
        )}

        {job && (
          <div className={`mt-3 rounded-lg border ${sub} p-2.5 text-[11px]`}>
            <div className="flex items-center gap-2">
              <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                job.state === 'running' ? 'bg-sky-500/15 text-sky-300 border border-sky-500/30'
                : job.state === 'done' ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'}`}>
                {job.state}
              </span>
              <span className="tabular-nums">{nf(job.filled, 4)} / {nf(job.target, 4)}</span>
              <span className={muted}>· {job.clips} klip</span>
              {job.avgPrice > 0 && (
                <span className={muted} title="Emirlerin gönderildiği ağırlıklı ortalama fiyat. Dolum fiyatı değil — dolumu pozisyon farkından ölçüyoruz.">
                  · ort. emir {nf(job.avgPrice)}
                </span>
              )}
              <span className={`ml-auto ${muted}`}>{job.reason}</span>
            </div>
            <div className="mt-1.5 h-1.5 rounded bg-white/10 overflow-hidden">
              <div className="h-full bg-sky-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}

        {msg && <p className={`mt-2 text-[11px] ${msg.ok ? 'text-emerald-400' : 'text-rose-400'}`}>{msg.text}</p>}

        <div className="flex items-center gap-2 mt-3">
          {!running ? (
            <button onClick={fire} disabled={busy || !taker || !sym}
              className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-600/25 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-600/40 disabled:opacity-40">
              {busy ? 'Başlatılıyor…' : 'Başlat — emir gönderir'}
            </button>
          ) : (
            <button onClick={() => sendWs({ type: 'hedge_stop', payload: { botId, symbol: sym } })}
              className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold bg-rose-600/25 border border-rose-500/50 text-rose-300 hover:bg-rose-600/40 flex items-center justify-center gap-1.5">
              <Square size={12} /> Durdur
            </button>
          )}
          <button onClick={onClose} className={`px-3 py-2 rounded-lg text-xs border ${sub} ${muted}`}>Kapat</button>
        </div>
      </div>
    </div>
  );
}
